// Rechnungsliste (RechnungList.tsx): was eine Zeile zeigt. Je Rechnung höchstens eine graue Zeile (Stand und ein
// Zusatz), höchstens ein Knopf für den nächsten Schritt, alles andere im Menü „···“ (ZeilenMenue.tsx). Nur Daten, kein
// React und kein Tarif: Rechte und fehlende Funktionen kommen im Kontext (Billtoro: darf, fehlendeFunktion; in der
// Kanzlei-App alles erlaubt). Den Empfänger einer E-Mail liest die Zeile nie, also keine E-Mail-Adressen in der Liste.
import { fmtDayMonth, kurzTag } from '../lib/format';
import { eurC } from '../lib/money';
import type { Fehlt } from './tarifHinweis';
import { MAHN_TITLES, type PostEintrag, type RtItem } from './types';
import { stundenText } from './zeiten/dauer';
import { zeitenText } from './zeiten/ZeitDaten';

export const isOpen = (i: RtItem) => i.kind === 'rechnung' && (i.status === 'offen' || i.status === 'teilweise');
export const isOverdue = (i: RtItem, today: string) => isOpen(i) && i.dueDate < today;
// Mahnen geht ab dem Tag nach der Fälligkeit, solange etwas offen ist, bis zur letzten Mahnung; die nächste Stufe erst
// nach der Frist der vorigen
export const lastMahn = (i: RtItem) => i.mahnungen[i.mahnungen.length - 1];
export const canMahn = (i: RtItem, today: string) => isOverdue(i, today) && i.offen > 0 && i.mahnungen.length < 3;
export const fristLaeuft = (i: RtItem, today: string) => !!lastMahn(i) && lastMahn(i)!.dueDate >= today;
// Rechnungsbetrag nach Rechnungskorrekturen
export const gemindert = (i: RtItem) => Math.round((i.amount - i.korrigiert) * 100) / 100;
// „Gutschrift / Korrektur“: Rechnung (keine Abschlagsrechnung), nicht storniert, noch etwas übrig
export const kannKorrektur = (i: RtItem) => i.kind === 'rechnung' && i.status !== 'storniert' && i.art !== 'abschlag' && gemindert(i) > 0;

// Mahnlauf in der Zeile: „Automatisch: Mahnung ab 24.10.“, heute fällig bzw. im nächsten Lauf, oder warum nicht
// automatisch (Mahnstopp an der Rechnung zeigt schon das Zeichen)
export function mahnlaufText(i: RtItem, modus: 'vorschlagen' | 'senden'): string | null {
  const m = i.mahnlauf;
  if (!m) return null;
  if ('grund' in m) {
    if (i.mahnstopp) return null;
    const grund = m.grund.replace(/\.$/, '');
    return grund.startsWith('Nicht automatisch') ? grund : `Nicht automatisch: ${grund}`;
  }
  const titel = MAHN_TITLES[m.stufe - 1];
  if (!m.faellig) return `Automatisch: ${titel} ab ${fmtDayMonth(m.ab)}`;
  return modus === 'vorschlagen' ? `${titel} heute fällig` : `${titel} geht im nächsten Lauf raus`;
}

export interface ZeilenKontext {
  // data.today, YYYY-MM-DD
  today: string;
  // Rechte des Logins (Billtoro: darf, sonst alles): Rechnungen, Zahlungen, Angebote und Aufträge
  rechnen: boolean;
  zahlen: boolean;
  angebote: boolean;
  // Brief per Post auf dem Server eingerichtet (Schalter in data.post)
  postAn: boolean;
  // Billtoro: Mahnungen (ab Stufe 2) bzw. Mahnläufe nicht im Tarif
  mahnFehlt: Fehlt;
  mahnlaufFehlt: boolean;
  // Mahnlauf eingeschaltet (Art), sonst null
  mahnlauf: 'vorschlagen' | 'senden' | null;
  // Abschlagsrechnung → Schlussrechnung, die sie abzieht
  abgezogenIn: ReadonlyMap<string, string>;
  // Text eines Briefs (postText aus PostDialog.tsx, übersetzt mit t())
  postText(p: PostEintrag): string;
}

// Nächster Schritt einer Zeile (der eine Knopf): Erstattung, Mahnen (Stufe 0–2; gesperrt: Billtoro ohne Mahnungen im
// Tarif), per E-Mail oder Zahlung eintragen
export type Schritt =
  | { art: 'erstattung' }
  | { art: 'mahnen'; stufe: 0 | 1 | 2; gesperrt: Exclude<Fehlt, null> | null }
  | { art: 'mail' }
  | { art: 'zahlung' }
  | null;

// Eintrag im Menü „···“ (Reihenfolge wie menueEintraege); mahnung und mahnungMail mit der Mahnung
export type Eintrag =
  | {
      art:
        | 'mail'
        | 'post'
        | 'xml'
        | 'stundenzettel'
        | 'zahlung'
        | 'erstattung'
        | 'zahlungen'
        | 'mahnen'
        | 'mahnstopp'
        | 'korrektur'
        | 'lieferschein'
        | 'kopieren'
        | 'abo'
        | 'trenner'
        | 'neu'
        | 'storno';
    }
  | { art: 'mahnung' | 'mahnungMail'; id: string };

// Knopf „… schreiben“ in der Liste (kurz wie im Bild, das Fenster und die PDF sagen „Zahlungserinnerung“)
export function mahnKnopf(stufe: 0 | 1 | 2): string {
  return ['Erinnerung schreiben', 'Mahnung schreiben', 'Letzte Mahnung schreiben'][stufe]!;
}

// Brief nicht angekommen: vom Anbieter abgelehnt bzw. nicht verschickt
const fehlgeschlagen = (p: PostEintrag) => p.status === 'fehler' || p.status === 'storniert';

// Hat die Rechnung die App verlassen? E-Mail, Brief (übergeben oder unterwegs), Teilen über das Menü des Geräts oder
// heruntergeladen
export function verschickt(i: RtItem): boolean {
  return i.versand.length > 0 || (i.post ?? []).some((p) => !fehlgeschlagen(p)) || !!i.geteilt;
}

// Mahnlauf an und er schickt (oder schlägt vor) die nächste Stufe selbst: dann kein roter Knopf „… schreiben“
export function mahnlaufKommt(i: RtItem, ctx: ZeilenKontext): boolean {
  return ctx.mahnlauf !== null && !!i.mahnlauf && 'stufe' in i.mahnlauf;
}

// „20.10.“ in diesem Jahr, sonst „20.10.2025“: kurzTag aus lib/format.ts, dieselbe Schreibweise in allen Listen
export { kurzTag };
const tag = kurzTag;

// Tag nach einem Datum (YYYY-MM-DD)
function tagDanach(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

// Wie die Rechnung verschickt ist: der neuere von E-Mail und Brief; ein Brief, der nach der E-Mail fehlschlug, steht
// rot dahinter. Sonst geteilt oder heruntergeladen, „noch nicht verschickt“ nur mit mitNicht.
function versandFakt(i: RtItem, ctx: ZeilenKontext, mitNicht: boolean): { teile: string[]; bad: boolean } {
  const e = i.versand[0];
  const p = i.post?.[0];
  if (p && (!e || Date.parse(p.am) > Date.parse(e.am))) {
    if (fehlgeschlagen(p) && e) return { teile: [`verschickt ${tag(e.am, ctx.today)}`, ctx.postText(p)], bad: true };
    return { teile: [ctx.postText(p)], bad: fehlgeschlagen(p) };
  }
  if (e) return { teile: [`verschickt ${tag(e.am, ctx.today)}`], bad: false };
  if (i.geteilt) return { teile: [`${i.geteilt.weg === 'teilen' ? 'geteilt' : 'heruntergeladen'} ${tag(i.geteilt.am, ctx.today)}`], bad: false };
  return { teile: mitNicht ? ['noch nicht verschickt'] : [], bad: false };
}

// Graue Zeile unter Kunde: Stand und höchstens ein Zusatz (Frist, Bezug oder Herkunft), null: keine Zeile. bad: rot
// (Brief fehlgeschlagen)
export function infoZeile(i: RtItem, ctx: ZeilenKontext): { teile: string[]; bad: boolean } | null {
  const w = i.waehrung;
  const fertig = (teile: (string | null)[], bad = false) => {
    const t = teile.filter((x): x is string => !!x);
    return t.length ? { teile: t, bad } : null;
  };
  // Stornorechnung und Rechnungskorrektur: Kunde, dazu wie sie verschickt ist
  if (i.kind !== 'rechnung') {
    const f = versandFakt(i, ctx, false);
    return fertig([i.customer, f.teile[f.teile.length - 1] ?? null], f.bad);
  }

  const nummern = i.korrekturen.map((k) => k.number).join(', ');
  let stand: string | null = null;
  // Stand nennt schon die Rechnungskorrekturen (dann kein Zusatz dazu)
  let mitKorrektur = false;
  let fakt: { teile: string[]; bad: boolean } | null = null;
  if (i.erstattung > 0) stand = `${eurC(i.erstattung, w)} an den Kunden zu erstatten`;
  else if (i.status === 'storniert') {
    if (i.korrekturen.length) {
      stand = `ganz gutgeschrieben (${nummern})`;
      mitKorrektur = true;
    } else fakt = versandFakt(i, ctx, false);
  } else if (i.status === 'bezahlt') {
    if (i.ausgebucht > 0) stand = `ausgebucht ${eurC(i.ausgebucht, w)}`;
    else if (i.korrekturen.length) {
      stand = `gutgeschrieben ${eurC(i.korrigiert, w)} (${nummern})`;
      mitKorrektur = true;
    } else if (i.paidOn) stand = `bezahlt am ${tag(i.paidOn, ctx.today)}`;
  } else if (i.status === 'teilweise') stand = `offen ${eurC(i.offen, w)} von ${eurC(i.forderung, w)}`;
  else if (i.forderung > gemindert(i)) stand = `mit Mahngebühr und Zinsen ${eurC(i.forderung, w)}`;
  // offen: „noch nicht verschickt“ nur ohne Mahnung (mit Mahnung ist dem Kunden schon geschrieben)
  else fakt = versandFakt(i, ctx, i.mahnungen.length === 0);
  if (fakt) {
    // Brief nach der E-Mail fehlgeschlagen: das ist der Zusatz
    if (fakt.teile.length > 1) return fertig(fakt.teile, fakt.bad);
    stand = fakt.teile[0] ?? null;
  }
  return fertig([stand, zusatz(i, ctx, stand, mitKorrektur)], fakt?.bad ?? false);
}

// Zusatz der Zeile: die erste passende Angabe, die nicht schon im Stand steht
function zusatz(i: RtItem, ctx: ZeilenKontext, stand: string | null, mitKorrektur: boolean): string | null {
  const w = i.waehrung;
  const m = lastMahn(i);
  const ueber = isOverdue(i, ctx.today);
  const ml = i.mahnlauf;
  const ab = i.vorgang?.aus.find((x) => x.art === 'auftrag');
  const ls = i.vorgang?.aus.filter((x) => x.art === 'lieferschein') ?? [];
  const dazu = i.vorgang?.lieferscheine ?? [];
  const abgezogen = ctx.abgezogenIn.get(i.number);
  const regeln: (() => string | null)[] = [
    // letzte Mahnung noch nicht per E-Mail (vielleicht gedruckt, das weiß die App nicht)
    () => (isOpen(i) && m && !m.versendetAm ? `${MAHN_TITLES[m.level - 1]} nicht per E-Mail verschickt` : null),
    () => (ueber && ctx.mahnlauf && ml && 'stufe' in ml ? mahnlaufText(i, ctx.mahnlauf) : null),
    // Frist der letzten Mahnung läuft noch: ab wann die nächste Stufe geht
    () => (ueber && canMahn(i, ctx.today) && fristLaeuft(i, ctx.today) ? `${MAHN_TITLES[i.mahnungen.length]} ab ${tag(tagDanach(m!.dueDate), ctx.today)}` : null),
    () => (ueber && ctx.mahnlauf && ml && 'grund' in ml && !i.mahnstopp ? mahnlaufText(i, ctx.mahnlauf) : null),
    () => (abgezogen ? `abgezogen in der Schlussrechnung ${abgezogen}${isOpen(i) ? ', bleibt trotzdem zu zahlen' : ''}` : null),
    () => (i.abzuege.length ? `abzüglich ${i.abzuege.length === 1 ? 'Abschlag' : 'Abschläge'} ${i.abzuege.join(', ')}` : null),
    () =>
      i.korrekturen.length && !mitKorrektur ? `gutgeschrieben ${eurC(i.korrigiert, w)} (${i.korrekturen.map((k) => k.number).join(', ')})` : null,
    // aus Lieferscheinen bzw. aus der Auftragsbestätigung geschrieben, Lieferscheine zur Rechnung
    () => (ls.length ? `aus ${ls.length === 1 ? 'Lieferschein' : 'Lieferscheinen'} ${ls.map((x) => x.number).join(', ')}` : null),
    () => (ab ? `aus Auftrag ${ab.number}` : null),
    () => (dazu.length ? `${dazu.length === 1 ? 'Lieferschein' : 'Lieferscheine'} ${dazu.map((x) => x.number).join(', ')} dazu` : null),
    // Billtoro: aus erfassten Zeiten geschrieben („aus 12 Zeiten (14:30 Std.)“)
    () => (i.zeiten ? `aus ${zeitenText(i.zeiten.anzahl)} (${stundenText(i.zeiten.minuten)})` : null),
    () => (i.auftrag ? `Auftrag ${i.auftrag}` : null),
  ];
  for (const r of regeln) {
    const t = r();
    if (t && t !== stand) return t;
  }
  return null;
}

// Der eine Knopf der Zeile (erste passende Regel). Ohne das Recht kein Knopf, nie ein gesperrter (außer der Marke des
// Tarifs bei Mahnungen, wie bisher).
export function naechsterSchritt(i: RtItem, ctx: ZeilenKontext): Schritt {
  if (i.kind !== 'rechnung') return null;
  if (i.erstattung > 0) return ctx.zahlen ? { art: 'erstattung' } : null;
  if (i.status === 'storniert' || i.status === 'bezahlt') return null;
  // fällige Mahnung, die der Mahnlauf nicht selbst schreibt
  if (ctx.rechnen && canMahn(i, ctx.today) && !fristLaeuft(i, ctx.today) && !mahnlaufKommt(i, ctx)) {
    const stufe = i.mahnungen.length as 0 | 1 | 2;
    // Billtoro: die Zahlungserinnerung ist in jedem Tarif, Mahnungen danach erst ab Start
    return { art: 'mahnen', stufe, gesperrt: stufe > 0 ? ctx.mahnFehlt : null };
  }
  if (ctx.rechnen && i.status === 'offen' && i.mahnungen.length === 0 && !verschickt(i)) return { art: 'mail' };
  if (ctx.zahlen && isOpen(i)) return { art: 'zahlung' };
  return null;
}

// Menü „···“: alles, was nicht der Knopf ist (Bedingungen wie früher in der Zeile). „Per E-Mail“ bleibt drin, auch nach
// dem Verschicken (noch einmal schicken); ein leeres Menü zeigt die Zeile nicht.
export function menueEintraege(i: RtItem, ctx: ZeilenKontext, schritt: Schritt): Eintrag[] {
  const e: Eintrag[] = [];
  const add = (art: Exclude<Eintrag, { id: string }>['art']) => e.push({ art });
  if (i.kind !== 'rechnung') {
    if (ctx.rechnen) add('mail');
    if (ctx.postAn && ctx.rechnen) add('post');
    if (i.eFormat) add('xml');
    return e;
  }
  const storniert = i.status === 'storniert';
  if (ctx.rechnen && !storniert && schritt?.art !== 'mail') add('mail');
  if (ctx.postAn && ctx.rechnen && !storniert) add('post');
  if (i.eFormat) add('xml');
  // Billtoro: Stundenzettel aus dem Stapel, aus denen die Rechnung entstanden ist
  if (i.stundenzettel) add('stundenzettel');
  if (schritt?.art !== 'zahlung' && schritt?.art !== 'erstattung') {
    if (ctx.zahlen && isOpen(i)) add('zahlung');
    else if (ctx.zahlen && i.erstattung > 0) add('erstattung');
    else if (i.zahlungen.length > 0) add('zahlungen');
  }
  // Mahnen nur hier, wenn der Mahnlauf die nächste Stufe selbst schreibt (sonst ist es der Knopf)
  if (ctx.rechnen && canMahn(i, ctx.today) && !fristLaeuft(i, ctx.today) && mahnlaufKommt(i, ctx)) add('mahnen');
  i.mahnungen.forEach((m, n) => {
    e.push({ art: 'mahnung', id: m.id });
    if (n === i.mahnungen.length - 1 && !m.versendetAm && isOpen(i) && ctx.rechnen) e.push({ art: 'mahnungMail', id: m.id });
  });
  if (ctx.rechnen && isOpen(i) && !ctx.mahnlaufFehlt && (ctx.mahnlauf !== null || i.mahnstopp)) add('mahnstopp');
  if (ctx.rechnen && kannKorrektur(i)) add('korrektur');
  // Lieferschein zur Rechnung (nicht zu Abschlägen)
  if (ctx.angebote && i.art !== 'abschlag' && !storniert) add('lieferschein');
  if (ctx.rechnen) add('kopieren');
  if (ctx.rechnen && i.art === 'normal' && !storniert && !i.aboId) add('abo');
  // ersetzen: Neu schreiben und Stornieren unter einem Strich
  const frei = !ctx.abgezogenIn.has(i.number) && i.korrekturen.length === 0;
  const neu = ctx.rechnen && isOpen(i) && frei;
  const storno = ctx.rechnen && !storniert && frei;
  if ((neu || storno) && e.length > 0) add('trenner');
  if (neu) add('neu');
  if (storno) add('storno');
  return e;
}
